import { ColorMatrixFilter, Container, Graphics, Sprite, Text, Ticker, type Texture } from 'pixi.js';
import type { PickActResult, PickOffer, PickPlayView, PlayOffer } from '@goodtime/shared';
import { AudioBus } from '../../audio/AudioBus';
import { pickDestination, pickLead, pickStart, pickTop, reducePick, type PickEvent, type PickView } from '../../logic/minigames/pick';
import { GAME_SOUNDS, prizeSound } from '../../logic/minigames/sounds';
import { BoardDialog } from '../board/BoardDialog';
import { BrassButton, layoutButtonRow } from '../board/BrassButton';
import { animate, COLOURS, displayStyle, ease, prefersReducedMotion, uiStyle } from '../board/ui';
import { glowTexture } from '../road/glow';
import type { MiniGameDeps, MiniGameEnd, MiniGameScreen, MiniGameSnapshot, PlayApi } from './types';

/** The parcels' art, wrapped and opened, by parcel (repeating past three). */
const PARCEL_ART = [
  { closed: 'road:pick_parcel_1', open: 'road:pick_parcel_1_open' },
  { closed: 'road:pick_parcel_2', open: 'road:pick_parcel_2_open' },
  { closed: 'road:pick_parcel_3', open: 'road:pick_parcel_3_open' },
];

interface Parcel {
  root: Container;
  /** Bobs and wobbles; the shadow stays on the floor. */
  body: Container;
  shadow: Graphics;
  glow: Sprite;
  sprite: Sprite;
  /** A drawn parcel until the art arrives. */
  drawn: Graphics;
  size: number;
}

/**
 * Pick a prize, drawn in Pixi on a board: three wrapped parcels, one with
 * the top prize. The server filled them when it offered the play; a tap
 * asks it to open that parcel and only that parcel's prize comes back, so
 * the others stay wrapped. {@link reducePick} holds the rules of the
 * screen; this draws them. The prize's coins fly into the wallet when the
 * player collects (or go to the run's pot for a lamp's game).
 */
export class PickDialog extends BoardDialog implements MiniGameScreen {
  private readonly lead: Text;
  private readonly amount: Text;
  private readonly note: Text;
  private readonly error: Text;
  private readonly collect: BrassButton;
  private readonly row = new Container();
  private readonly dimmed = new ColorMatrixFilter();
  private parcels: Parcel[] = [];
  private state: PickView | null = null;
  private api: PlayApi | null = null;
  private done: ((end: MiniGameEnd) => void) | null = null;
  private closing = false;
  private clock = 0;
  private laidOutWidth = 0;
  private readonly tick = (ticker: Ticker) => this.animateParcels(ticker.deltaMS);

  constructor(private readonly deps: MiniGameDeps) {
    super({ title: 'PICK A PRIZE', floor: deps.floor(), maxWidth: 420 });
    this.lead = new Text({ text: '', style: { ...uiStyle(17), align: 'center', wordWrap: true } });
    this.lead.anchor.set(0.5, 0);
    this.amount = new Text({ text: '', style: displayStyle(32) });
    this.amount.anchor.set(0.5, 0);
    this.note = new Text({ text: '', style: { ...uiStyle(14, 0xf0dcb4, '500'), align: 'center' } });
    this.note.anchor.set(0.5, 0);
    this.error = new Text({ text: '', style: { ...uiStyle(14, COLOURS.loss, '600'), align: 'center', wordWrap: true } });
    this.error.anchor.set(0.5, 0);
    this.collect = new BrassButton({ label: 'COLLECT', kind: 'gold', onTap: () => void this.onCollect() });
    // The parcels not picked stay wrapped, a little faded: still parcels.
    this.dimmed.saturate(-0.35);
    this.dimmed.brightness(0.85, true);
    this.content.addChild(this.lead, this.row, this.amount, this.note, this.error, this.collect);
  }

  play(offer: PlayOffer, api: PlayApi): Promise<MiniGameEnd> {
    this.api = api;
    this.closing = false;
    this.state = pickStart(offer as PickOffer);
    this.setFloor(this.deps.floor());
    this.buildParcels(this.state.boxes);
    this.render();
    Ticker.shared.add(this.tick);
    void this.open();
    return new Promise((resolve) => (this.done = resolve));
  }

  /** Re-lay the game when the board's width changes (a resize). */
  override layout(viewWidth: number, viewHeight: number): void {
    super.layout(viewWidth, viewHeight);
    if (this.state && this.contentWidth !== this.laidOutWidth) this.render();
  }

  get snapshot(): MiniGameSnapshot {
    const s = this.state;
    return {
      kind: 'pick',
      open: this.visible,
      title: this.titleText,
      lead: this.lead.text,
      pieces: this.parcels.map((_, i) =>
        s?.opened?.box === i ? `Parcel ${i + 1}: ${s.opened.coins} coins` : `Parcel ${i + 1}`,
      ),
      result: this.amount.visible ? this.amount.text : '',
      note: this.note.visible ? this.note.text : '',
      buttons: this.collect.visible ? [this.collect.labelText] : [],
    };
  }

  press(label: string): boolean {
    if (!this.collect.visible || !this.collect.labelText.startsWith(label)) return false;
    this.collect.onTap?.();
    return true;
  }

  tap(index: number): boolean {
    if (!this.visible || index < 0 || index >= this.parcels.length) return false;
    this.dispatch({ type: 'tap', box: index });
    return true;
  }

  // ---- the game -------------------------------------------------------

  private dispatch(event: PickEvent): void {
    if (!this.state) return;
    const before = this.state;
    const { view, effect } = reducePick(before, event);
    this.state = view;
    this.render();
    if (before.phase !== 'opened' && view.phase === 'opened') void this.revealOpened();
    if (effect?.type === 'act') void this.send(effect.arg);
  }

  private async send(arg: string): Promise<void> {
    const api = this.api;
    if (!api) return;
    try {
      const result = (await api.act(0, 'pick', arg)) as PickActResult;
      this.dispatch({ type: 'answered', result });
    } catch {
      const play = (await api.recover().catch(() => null)) as PickPlayView | null;
      this.dispatch({ type: 'failed', play });
    }
  }

  /** The chosen parcel bursts open, then the prize sounds. */
  private async revealOpened(): Promise<void> {
    const s = this.state;
    if (!s?.prize) return;
    AudioBus.shared.play(GAME_SOUNDS.parcelOpen, { volume: 0.85 });
    window.setTimeout(() => AudioBus.shared.play(prizeSound(s.prize!.coins, s.maxValue), { volume: 0.75 }), 380);
    const parcel = s.opened ? this.parcels[s.opened.box] : null;
    if (!parcel) return;
    // Every prize here is a win: confetti from the parcel.
    void this.celebrate({ x: this.row.x + parcel.root.x, y: this.row.y + parcel.root.y });
    parcel.glow.visible = true;
    await animate(460, (t) => {
      parcel.body.scale.set(0.82 + 0.24 * ease.outBack(t));
      parcel.glow.alpha = Math.min(1, t * 2) * 0.9;
      parcel.glow.scale.set((parcel.size / 64) * (0.6 + 1.0 * ease.outCubic(t)));
    });
  }

  private async onCollect(): Promise<void> {
    const s = this.state;
    if (!s?.prize || this.closing) return;
    this.closing = true;
    this.collect.setEnabled(false);
    if (!s.toPot && s.prize.coins > 0) await this.deps.flyCoins(this.amount.getGlobalPosition(), s.prize.coins);
    Ticker.shared.remove(this.tick);
    await this.close();
    this.collect.setEnabled(true);
    const done = this.done;
    this.done = null;
    done?.({ prize: s.prize, pot: s.pot });
  }

  // ---- drawing --------------------------------------------------------

  private buildParcels(count: number): void {
    this.row.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.parcels = Array.from({ length: count }, (_, i) => {
      const root = new Container();
      const body = new Container();
      const shadow = new Graphics();
      const glow = new Sprite(glowTexture());
      glow.anchor.set(0.5);
      glow.blendMode = 'add';
      glow.tint = 0xffd36b;
      glow.visible = false;
      const sprite = new Sprite();
      sprite.anchor.set(0.5);
      const drawn = new Graphics();
      body.addChild(drawn, sprite);
      root.addChild(shadow, glow, body);
      root.eventMode = 'static';
      root.cursor = 'pointer';
      root.on('pointertap', () => this.dispatch({ type: 'tap', box: i }));
      root.on('pointerover', () => {
        if (this.state?.phase === 'choose') body.scale.set(1.06);
      });
      root.on('pointerout', () => {
        if (this.state?.phase === 'choose') body.scale.set(1);
      });
      this.row.addChild(root);
      return { root, body, shadow, glow, sprite, drawn, size: 100 };
    });
  }

  /** Lay the dialog out for where the game is. */
  private render(): void {
    const s = this.state;
    if (!s) return;
    const w = this.contentWidth;
    this.laidOutWidth = w;
    let y = 0;

    this.lead.style.wordWrapWidth = w;
    this.lead.text = pickLead(s);
    this.lead.style.fill = s.phase === 'opened' && pickTop(s) ? COLOURS.gold : COLOURS.cream;
    this.lead.position.set(w / 2, y);
    y += this.lead.height + 10;

    // The parcels, as big as the board allows.
    const gap = 6;
    const size = Math.floor(Math.min(124, (w - gap * (s.boxes - 1)) / s.boxes));
    const rowW = size * s.boxes + gap * (s.boxes - 1);
    this.parcels.forEach((p, i) => {
      p.size = size;
      const art = PARCEL_ART[i % PARCEL_ART.length]!;
      const isOpen = s.opened?.box === i;
      const texture = this.deps.texture(isOpen ? art.open : art.closed);
      this.paintParcel(p, texture, i);
      p.root.position.set((w - rowW) / 2 + i * (size + gap) + size / 2, y + size / 2);
      p.root.hitArea = { contains: (x: number, yy: number) => Math.abs(x) <= size / 2 && Math.abs(yy) <= size / 2 };
      const settled = s.phase === 'opened';
      p.root.cursor = s.phase === 'choose' ? 'pointer' : 'default';
      p.root.alpha = settled && !isOpen ? 0.7 : 1;
      p.body.filters = settled && !isOpen ? [this.dimmed] : [];
      if (!isOpen) p.glow.visible = false;
      if (s.phase !== 'choose' && !isOpen && s.busy !== i) p.body.scale.set(1);
    });
    y += size + 10;

    // What it paid, and where it went.
    const opened = s.phase === 'opened' && s.prize;
    this.amount.visible = this.note.visible = !!opened;
    if (opened && s.prize) {
      this.amount.text = `+${s.prize.coins.toLocaleString('en-GB')} coins`;
      this.amount.style = displayStyle(pickTop(s) ? 34 : 30);
      this.amount.position.set(w / 2, y);
      y += this.amount.height + 2;
      this.note.text = pickDestination(s);
      this.note.position.set(w / 2, y);
      y += this.note.height + 12;
    }

    this.error.visible = !!s.error;
    if (s.error) {
      this.error.style.wordWrapWidth = w;
      this.error.text = s.error;
      this.error.position.set(w / 2, y);
      y += this.error.height + 8;
    }

    this.collect.visible = !!opened;
    if (opened) {
      layoutButtonRow(null, this.collect, 0, y, w);
      y += this.collect.buttonHeight + 4;
    }
    this.setContentHeight(y);
  }

  /** The parcel's art at `size`, or a drawn stand-in while it loads. */
  private paintParcel(p: Parcel, texture: Texture | null, index: number): void {
    const { size } = p;
    p.shadow
      .clear()
      .ellipse(0, size * 0.4, size * 0.32, size * 0.065)
      .fill({ color: 0x000000, alpha: 0.45 });
    if (texture) {
      p.sprite.texture = texture;
      p.sprite.width = p.sprite.height = size;
      p.sprite.visible = true;
      p.drawn.clear();
      return;
    }
    p.sprite.visible = false;
    const box = size * 0.56;
    const paper = [0xc98a4a, 0x2f7a34, 0xf2b81e][index % 3]!;
    p.drawn
      .clear()
      .roundRect(-box / 2, -box / 2 + size * 0.06, box, box, 8)
      .fill({ color: paper })
      .stroke({ color: 0x0b1838, width: 3 })
      .rect(-box * 0.08, -box / 2 + size * 0.06, box * 0.16, box)
      .fill({ color: 0xffe08a });
  }

  /** Idle parcels bob; the one being opened wobbles until the server
   *  answers. Still, with reduced motion. */
  private animateParcels(deltaMs: number): void {
    const s = this.state;
    if (!s || prefersReducedMotion()) return;
    this.clock += deltaMs / 1000;
    this.parcels.forEach((p, i) => {
      if (s.phase === 'choose') {
        p.body.y = Math.sin(this.clock * 2.6 + i * 1.1) * 4;
        p.body.rotation = 0;
      } else if (s.busy === i) {
        p.body.y = 0;
        p.body.rotation = Math.sin(this.clock * 24) * 0.09;
      } else {
        p.body.y = 0;
        p.body.rotation = 0;
      }
    });
  }
}
